import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pencil, Plus, Search, X } from "lucide-react";
import api from "../../../api/axios";
import { useToast } from "../../../context/ToastContext";
import { ErrorMessage, validate } from "../../../components/validation/Validation";

const rules = {
  user_name: {
    required: true,
    minLength: 3,
    label: "User Name",
  },
  org_name: {
    required: true,
    label: "Organization Name",
  },
  email: {
    required: true,
    type: "email",
    label: "Email",
  },
  password: {
    required: true,
    minLength: 8,
    label: "Password",
  },
  mobile: {
    required: true,
    pattern: /^[0-9]{10}$/,
    label: "Mobile Number",
    message: "Mobile number must be exactly 10 digits.",
  },
  sub_plan: {
    required: true,
    label: "Subscription Plan",
  },
};

export default function OrganizationsPage() {
  const [organizations, setOrganizations] = useState([]);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [form, setForm] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOrganization, setEditOrganization] = useState(null);
  const [errors, setErrors] = useState({});

  const { success, error } = useToast();

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await api.get("/user/list?role=organization");
      if (response && response.data && response.data.success) {
        setOrganizations(response.data?.data?.list || []);
      } else {
        setLoading(false);
        setOrganizations([]);
      }
    } catch (err) {
      setLoading(false);
      console.log(err?.data?.message);
    }
  };

  const rows = useMemo(
    () =>
      organizations.filter(
        (item) =>
          (item.user_name || "")
            .toLowerCase()
            .includes(search.toLowerCase()) &&
          (!plan || item.subscription?.plan === plan)
      ),
    [organizations, search, plan]
  );

  const saveOrganization = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);

      const form = Object.fromEntries(
        new FormData(event.currentTarget)
      );

      const validationErrors = validate(form, rules);

      setErrors(validationErrors);

      if (Object.keys(validationErrors).length > 0) {
        return;
      }

      const payload = {
        user_name: form.user_name,
        org_name: form.org_name,
        email: form.email,
        password: form.password,
        mobile: form.mobile,
        role: form.role,
        sub_plan: form.sub_plan,
      }

      if (payload.sub_plan === "ENTERPRISE") {
        payload.plan_limits = {
          users: Number(form.users_limit),
          creatives: Number(form.creatives_limit),
          campaigns: Number(form.campaigns_limit),
          brands: Number(form.brands_limit),
        };
      }

      const response = await api.post("/user/save", payload);
      if (response && response.data && response.data.success) {
        setForm(null);
        loadOrganizations();
        success("Organization created successfully.");
      } else {
        setLoading(false);
        error(response.data?.message || "Failed to save organization.");
      }
    } catch (err) {
      setLoading(false);
      error(err?.data?.message);
    }
  };

  const handleValidationFormChange = (e) => {
    const { name, value } = e.target;

    setErrors((prev) => {
      if (!prev[name]) return prev;

      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h2 className="text-2xl font-bold">Organizations</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage customer organizations and initial subscriptions.
          </p>
        </div>
        <button
          onClick={() => setForm({})}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus size={17} />
          Create organization
        </button>
      </div>
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex gap-3 border-b border-slate-100 p-4">
          <label className="relative flex-1">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organizations…"
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm"
            />
          </label>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 text-sm"
          >
            <option value="">All Plans</option>
            <option value="FREE">FREE</option>
            <option value="STARTER">STARTER</option>
            <option value="PRO">PRO</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {[
                  "Organization",
                  "Plan",
                  "Users",
                  "Status",
                  "Subscription",
                  "Created",
                  "Updated",
                  "Actions",
                ].map((x) => (
                  <th key={x} className="px-5 py-3">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50">

                  {/* Organization */}
                  <td className="px-5 py-4">
                    <div className="font-semibold">
                      {item.user_name || "-"}
                    </div>

                    <div className="text-xs text-slate-500">
                      {item.email}
                    </div>
                  </td>

                  {/* Plan */}
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${{
                        FREE: "bg-green-100 text-green-700",
                        STARTER: "bg-blue-100 text-blue-700",
                        PRO: "bg-violet-100 text-violet-700",
                        ENTERPRISE: "bg-amber-100 text-amber-700",
                      }[item.subscription?.plan] ||
                        "bg-slate-100 text-slate-700"
                        }`}
                    >
                      {item.subscription?.plan || "-"}
                    </span>
                  </td>

                  {/* Users */}
                  <td className="px-5 py-4">
                    {item.subscription?.usage?.users || 0} /
                    {" "}
                    {item.subscription?.limits?.users || 0}
                  </td>

                  {/* Status */}
                  <td className="px-5 py-4">
                    <OrganizationStatus value={item.status} />
                  </td>

                  {/* Subscription */}
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${item.subscription?.status === "ACTIVE"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-red-100 text-red-700"
                        }`}
                    >
                      {item.subscription?.status}
                    </span>
                  </td>

                  {/* Created */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    {new Date(item.created_on).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}

                    <div className="text-xs text-slate-500">
                      {new Date(item.created_on).toLocaleTimeString("en-IN")}
                    </div>
                  </td>

                  <td className="px-5 py-4 whitespace-nowrap">
                    {new Date(item.updated_on).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}

                    <div className="text-xs text-slate-500">
                      {new Date(item.updated_on).toLocaleTimeString("en-IN")}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-4">
                    <button
                      onClick={() => setSelected(item)}
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => setEditOrganization(item)}
                      className="rounded-lg p-2 hover:bg-indigo-100 text-indigo-600"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {form && (
        <OrganizationForm
          onClose={() => setForm(null)}
          onSave={saveOrganization}
          errors={errors}
          handleFormChange={handleValidationFormChange}
        />
      )}
      {editOrganization && (
        <OrganizationEdit
          value={editOrganization}
          onClose={() => setEditOrganization(null)}
          onUpdate={() => {
            setEditOrganization(null);
            loadOrganizations();
            // Refresh the organization list or update the specific organization
          }}
        />
      )}
      {selected && (
        <OrganizationDetails
          value={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

const OrganizationStatus = ({ value }) => (
  <span
    className={`rounded-full px-3 py-1 text-xs font-semibold ${value === "ACTIVE"
      ? "bg-emerald-100 text-emerald-700"
      : value === "SUSPENDED"
        ? "bg-yellow-100 text-yellow-700"
        : "bg-red-100 text-red-700"
      }`}
  >
    {value}
  </span>
);

function OrganizationForm({ onClose, onSave, errors, handleFormChange }) {
  const [selectedPlan, setSelectedPlan] = React.useState("FREE");

  const handlePlanChange = (e) => {
    const value = e.target.value;

    setSelectedPlan(value);

    // Keep your existing form change logic
    handleFormChange(e);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 !m-0 !h-full">
      <form
        onSubmit={onSave}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold">
              Create Organization
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Create a new organization and assign a subscription plan.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="grid gap-5 p-6 sm:grid-cols-2">

          {/* User Name */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              User Name <span className="text-red-500">*</span>
            </label>

            <input
              name="user_name"
              id="user_name"
              placeholder="Enter user name"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />

            <ErrorMessage error={errors.user_name} />
          </div>

          {/* Organization */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Organization Name{" "}
              <span className="text-red-500">*</span>
            </label>

            <input
              name="org_name"
              placeholder="Enter organization"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />

            <ErrorMessage error={errors.org_name} />
          </div>

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Email <span className="text-red-500">*</span>
            </label>

            <input
              type="email"
              name="email"
              placeholder="Enter email"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />

            <ErrorMessage error={errors.email} />
          </div>

          {/* Password */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Password <span className="text-red-500">*</span>
            </label>

            <input
              type="password"
              name="password"
              placeholder="Enter password"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />

            <ErrorMessage error={errors.password} />
          </div>

          {/* Mobile */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Mobile <span className="text-red-500">*</span>
            </label>

            <input
              name="mobile"
              maxLength={10}
              placeholder="9999999999"
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            />

            <ErrorMessage error={errors.mobile} />
          </div>

          {/* Subscription Plan */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Subscription Plan{" "}
              <span className="text-red-500">*</span>
            </label>

            <select
              name="sub_plan"
              value={selectedPlan}
              onChange={handlePlanChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
            >
              <option value="FREE">FREE</option>
              <option value="STARTER">STARTER</option>
              <option value="PRO">PRO</option>
              <option value="ENTERPRISE">ENTERPRISE</option>
            </select>

            <ErrorMessage error={errors.sub_plan} />
          </div>

          {/* ================================================= */}
          {/* CUSTOM PLAN LIMITS */}
          {/* ================================================= */}

          {selectedPlan === "ENTERPRISE" && (
            <div className="sm:col-span-2">

              <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-5">

                {/* Section Header */}
                <div className="mb-5">

                  <h4 className="text-base font-semibold text-slate-900">
                    Custom Plan Limits
                  </h4>

                  <p className="mt-1 text-sm text-slate-500">
                    Set the resource limits for this organization's
                    custom subscription.
                  </p>

                </div>

                <div className="grid gap-5 sm:grid-cols-2">

                  {/* Users Limit */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Users Limit{" "}
                      <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="number"
                      name="users_limit"
                      min="1"
                      placeholder="Enter users limit"
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
                    />

                    <ErrorMessage
                      error={errors.users_limit}
                    />
                  </div>

                  {/* Creatives Limit */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Creatives Limit{" "}
                      <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="number"
                      name="creatives_limit"
                      min="1"
                      placeholder="Enter creatives limit"
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
                    />

                    <ErrorMessage
                      error={errors.creatives_limit}
                    />
                  </div>

                  {/* Campaigns Limit */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Campaigns Limit{" "}
                      <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="number"
                      name="campaigns_limit"
                      min="1"
                      placeholder="Enter campaigns limit"
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
                    />

                    <ErrorMessage
                      error={errors.campaigns_limit}
                    />
                  </div>

                  {/* Brand Limit */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Brand Limit{" "}
                      <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="number"
                      name="brands_limit"
                      min="1"
                      placeholder="Enter brand limit"
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none"
                    />

                    <ErrorMessage
                      error={errors.brands_limit}
                    />
                  </div>

                </div>
              </div>

            </div>
          )}

        </div>

        {/* Hidden Role */}
        <input
          type="hidden"
          name="role"
          value="organization"
        />

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-6 py-4">

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Create Organization
          </button>

        </div>
      </form>
    </div>
  );
}

function OrganizationEdit({ value, onClose, onUpdate }) {
  const { success, error } = useToast();

  const [form, setForm] = useState({
    user_name: value?.user_name || "",
    mobile: value?.mobile || "",
    status: value?.status || "ACTIVE",

    // Subscription
    sub_plan:
      value?.subscription?.plan ||
      value?.sub_plan ||
      "FREE",

    // Custom limits
    users_limit:
      value?.subscription?.limits?.users ??
      value?.users_limit ??
      "",

    creatives_limit:
      value?.subscription?.limits?.creatives ??
      value?.creatives_limit ??
      "",

    campaigns_limit:
      value?.subscription?.limits?.campaigns ??
      value?.campaigns_limit ??
      "",

    brands_limit:
      value?.subscription?.limits?.brands ??
      value?.brands_limit ??
      "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const currentPlan = value?.subscription?.plan || value?.sub_plan || "FREE";
      const isPlanChange = currentPlan !== form.sub_plan;

      const payload = {
        user_name: form.user_name,
        mobile: form.mobile,
        status: form.status,
        sub_plan: form.sub_plan,
        is_plan_change: isPlanChange,
        org_id: value?.org_id,
        sub_id: value?.subscription?.id || value?.sub_id,
      };

      // Only send custom limits when CUSTOM plan is selected
      if (form.sub_plan === "ENTERPRISE") {
        payload.plan_limits = {
          users: Number(form.users_limit),
          creatives: Number(form.creatives_limit),
          campaigns: Number(form.campaigns_limit),
          brands: Number(form.brands_limit),
        };
      }

      await api.put(
        `/user/${value.id}`,
        payload
      );

      success(
        "Organization updated successfully."
      );

      onUpdate?.();
      onClose();
    } catch (err) {
      console.log(err);
      error(
        err?.response?.data?.message ||
        "Failed to update organization."
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 !m-0 !h-full">

      <form
        onSubmit={handleUpdate}
        className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
      >

        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div className="flex items-center justify-between border-b px-6 py-4">

          <div>
            <h3 className="text-xl font-semibold text-slate-900">
              Edit Organization
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Update organization details and subscription settings.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={18} />
          </button>

        </div>

        {/* ================================================= */}
        {/* BODY */}
        {/* ================================================= */}

        <div className="max-h-[70vh] overflow-y-auto">

          <div className="grid gap-5 p-6 sm:grid-cols-2">

            {/* User Name */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                User Name
              </label>

              <input
                name="user_name"
                value={form.user_name}
                onChange={handleChange}
                placeholder="Enter user name"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            {/* Email */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Email
              </label>

              <input
                value={value?.email || ""}
                disabled
                className="w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-100 px-3 py-2.5 text-slate-500"
              />

              <p className="mt-1 text-xs text-slate-400">
                Email cannot be changed.
              </p>
            </div>

            {/* Mobile */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Mobile
              </label>

              <input
                name="mobile"
                value={form.mobile}
                maxLength={10}
                onChange={handleChange}
                placeholder="9999999999"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            {/* Status */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Status
              </label>

              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="ACTIVE">
                  ACTIVE
                </option>

                <option value="INACTIVE">
                  INACTIVE
                </option>

                <option value="SUSPENDED">
                  SUSPENDED
                </option>
              </select>
            </div>

            {/* ================================================= */}
            {/* SUBSCRIPTION PLAN */}
            {/* ================================================= */}

            <div className="sm:col-span-2">

              <label className="mb-2 block text-sm font-medium text-slate-700">
                Subscription Plan
              </label>

              <select
                name="sub_plan"
                value={form.sub_plan}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="FREE">
                  FREE
                </option>

                <option value="STARTER">
                  STARTER
                </option>

                <option value="PRO">
                  PRO
                </option>

                <option value="ENTERPRISE">
                  ENTERPRISE
                </option>
              </select>

              <p className="mt-1 text-xs text-slate-400">
                Changing the plan may change the organization's resource limits.
              </p>

            </div>

            {/* ================================================= */}
            {/* CUSTOM PLAN LIMITS */}
            {/* ================================================= */}

            {form.sub_plan === "ENTERPRISE" && (
              <div className="sm:col-span-2">

                <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-5">

                  {/* Header */}

                  <div className="mb-5">

                    <div className="flex items-center gap-2">

                      <h4 className="text-base font-semibold text-slate-900">
                        Custom Plan Limits
                      </h4>

                      <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
                        CUSTOM
                      </span>

                    </div>

                    <p className="mt-1 text-sm text-slate-500">
                      Define the maximum resources this organization can use.
                    </p>

                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">

                    {/* Users Limit */}

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Users Limit
                        <span className="ml-1 text-red-500">
                          *
                        </span>
                      </label>

                      <input
                        type="number"
                        name="users_limit"
                        min="1"
                        value={form.users_limit}
                        onChange={handleChange}
                        placeholder="e.g. 10"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />

                      <p className="mt-1 text-xs text-slate-400">
                        Maximum number of users.
                      </p>
                    </div>

                    {/* Creatives Limit */}

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Creatives Limit
                        <span className="ml-1 text-red-500">
                          *
                        </span>
                      </label>

                      <input
                        type="number"
                        name="creatives_limit"
                        min="1"
                        value={
                          form.creatives_limit
                        }
                        onChange={handleChange}
                        placeholder="e.g. 100"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />

                      <p className="mt-1 text-xs text-slate-400">
                        Maximum number of creatives.
                      </p>
                    </div>

                    {/* Campaigns Limit */}

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Campaigns Limit
                        <span className="ml-1 text-red-500">
                          *
                        </span>
                      </label>

                      <input
                        type="number"
                        name="campaigns_limit"
                        min="1"
                        value={
                          form.campaigns_limit
                        }
                        onChange={handleChange}
                        placeholder="e.g. 20"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />

                      <p className="mt-1 text-xs text-slate-400">
                        Maximum number of campaigns.
                      </p>
                    </div>

                    {/* Brand Limit */}

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Brand Limit
                        <span className="ml-1 text-red-500">
                          *
                        </span>
                      </label>

                      <input
                        type="number"
                        name="brands_limit"
                        min="1"
                        value={form.brands_limit}
                        onChange={handleChange}
                        placeholder="e.g. 5"
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />

                      <p className="mt-1 text-xs text-slate-400">
                        Maximum number of brands.
                      </p>
                    </div>

                  </div>

                </div>

              </div>
            )}

          </div>

        </div>

        {/* ================================================= */}
        {/* FOOTER */}
        {/* ================================================= */}

        <div className="flex justify-end gap-3 border-t bg-slate-50 px-6 py-4">

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Update Organization
          </button>

        </div>

      </form>

    </div>
  );
}

function OrganizationDetails({ value, onClose }) {
  const subscription = value?.subscription;

  const usage = subscription?.usage || {};
  const limits = subscription?.limits || {};

  const plan = subscription?.plan || "-";

  const planStyles = {
    FREE: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    STARTER: "bg-blue-50 text-blue-700 ring-blue-200",
    PRO: "bg-violet-50 text-violet-700 ring-violet-200",
    ENTERPRISE: "bg-amber-50 text-amber-700 ring-amber-200",
  };

  const getPercentage = (used, limit) => {
    if (!limit || limit <= 0) return 0;

    return Math.min(
      Math.round((used / limit) * 100),
      100
    );
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) {
      return "bg-red-500";
    }

    if (percentage >= 70) {
      return "bg-amber-500";
    }

    return "bg-indigo-500";
  };

  const usageItems = [
    {
      key: "users",
      label: "Users",
      icon: "👥",
      used: usage.users ?? 0,
      limit: limits.users ?? 0,
    },
    {
      key: "creatives",
      label: "Creatives",
      icon: "🎨",
      used: usage.creatives ?? 0,
      limit: limits.creatives ?? 0,
    },
    {
      key: "campaigns",
      label: "Campaigns",
      icon: "📢",
      used: usage.campaigns ?? 0,
      limit: limits.campaigns ?? 0,
    },
    {
      key: "brands",
      label: "Brands",
      icon: "🏷️",
      used: usage.brands ?? 0,
      limit: limits.brands ?? 0,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 !m-0 !h-full">

      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">

        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div className="border-b border-slate-200 px-6 py-5">

          <div className="flex items-start justify-between">

            <div className="flex items-center gap-4">

              {/* Avatar */}

              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-lg font-bold text-indigo-700">
                {(value?.user_name || "U")
                  .charAt(0)
                  .toUpperCase()}
              </div>

              <div>

                <h3 className="text-lg font-bold text-slate-900">
                  {value?.user_name || "-"}
                </h3>

                <p className="mt-0.5 text-sm text-slate-500">
                  {value?.email || "-"}
                </p>

              </div>

            </div>

            <button
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={19} />
            </button>

          </div>

          {/* Plan */}

          <div className="mt-5 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">

            <div>

              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Current Plan
              </p>

              <p className="mt-1 text-sm text-slate-600">
                Subscription & usage limits
              </p>

            </div>

            <span
              className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-bold ring-1 ${planStyles[plan] ||
                "bg-slate-100 text-slate-700 ring-slate-200"
                }`}
            >
              {plan}
            </span>

          </div>

        </div>

        {/* ================================================= */}
        {/* CONTENT */}
        {/* ================================================= */}

        <div className="max-h-[65vh] overflow-y-auto px-6 py-6">

          {/* Usage Header */}

          <div className="mb-5">

            <h4 className="text-base font-bold text-slate-900">
              Resource Usage
            </h4>

            <p className="mt-1 text-sm text-slate-500">
              See how much of the organization's plan is being used.
            </p>

          </div>

          {/* Usage Grid */}

          <div className="grid gap-4 sm:grid-cols-2">

            {usageItems.map((item) => {

              const percentage = getPercentage(
                item.used,
                item.limit
              );

              const isUnlimited =
                !item.limit || item.limit <= 0;

              return (
                <div
                  key={item.key}
                  className="rounded-xl border border-slate-200 bg-white p-4 transition hover:border-slate-300 hover:shadow-sm"
                >

                  {/* Card Header */}

                  <div className="flex items-center justify-between">

                    <div className="flex items-center gap-3">

                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-base">
                        {item.icon}
                      </div>

                      <div>

                        <p className="text-sm font-semibold text-slate-800">
                          {item.label}
                        </p>

                        <p className="text-xs text-slate-400">
                          {isUnlimited
                            ? "Unlimited"
                            : `${percentage}% used`}
                        </p>

                      </div>

                    </div>

                    <div className="text-right">

                      <p className="text-base font-bold text-slate-900">
                        {item.used}
                        {!isUnlimited && (
                          <span className="font-normal text-slate-400">
                            {" / "}
                            {item.limit}
                          </span>
                        )}
                      </p>

                    </div>

                  </div>

                  {/* Progress */}

                  {!isUnlimited && (
                    <div className="mt-4">

                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">

                        <div
                          className={`h-full rounded-full transition-all ${getProgressColor(
                            percentage
                          )}`}
                          style={{
                            width: `${percentage}%`,
                          }}
                        />

                      </div>

                      <div className="mt-1.5 flex justify-between text-[11px] text-slate-400">

                        <span>
                          {item.used} used
                        </span>

                        <span>
                          {item.limit - item.used > 0
                            ? `${item.limit - item.used} remaining`
                            : "Limit reached"}
                        </span>

                      </div>

                    </div>
                  )}

                </div>
              );
            })}

          </div>
        </div>

        {/* ================================================= */}
        {/* FOOTER */}
        {/* ================================================= */}

        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">

          <p className="text-xs text-slate-400">
            Organization details
          </p>

          <button
            onClick={onClose}
            className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Close
          </button>

        </div>

      </div>

    </div>
  );
}
